import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import logo from '../../assets/images/logo.png';
import { useAuth } from "../context/AuthContext";
import { Drawer, List, ListItem, ListItemIcon, ListItemText, Collapse, Button, Avatar, Tooltip, IconButton } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import BookIcon from '@mui/icons-material/Book';
import EventIcon from '@mui/icons-material/Event';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PersonIcon from '@mui/icons-material/Person';
import ReviewsIcon from '@mui/icons-material/Reviews';
import PaletteIcon from '@mui/icons-material/Palette';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LogoutIcon from '@mui/icons-material/Logout';
import InfoIcon from '@mui/icons-material/Info';
import LiveHelpIcon from '@mui/icons-material/LiveHelp';
import Collections from "@mui/icons-material/Collections";
import ScheduleIcon from '@mui/icons-material/Schedule';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HistoryIcon from '@mui/icons-material/History';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';

const NavBar = () => {
  const [activeSection, setActiveSection] = useState("");
  const [submenuOpen, setSubmenuOpen] = useState(null);
  const { logout, userData, themeMode, toggleTheme } = useAuth();
  const isDark = themeMode === 'dark';
  const location = useLocation();
  const navigate = useNavigate();

  const subMenuItems = useMemo(() => ({
    Tableghaat: [
      { id: 1, name: "Noor Diary", nav: "noor-diary", permission: "Noor-Diary" },
      { id: 2, name: "Mujjalah", nav: "mujjalah", permission: "Mujjalah" },
      { id: 3, name: "Matbuaat", nav: "books", permission: "Books" },
    ],
    Munasibaat: [
      { id: 1, name: "Munasibaat", nav: "munasibaat", permission: "Munasibaat" }
    ],
    Exhibition: [
      { id: 1, name: "Exhibition", nav: "exhibition", permission: "Exhibition" },
      { id: 2, name: "Content", nav: "exhibitionContent", permission: "ExhibitionContent" },
    ]
  }), []);

  const menuItems = useMemo(
    () => [
      { id: 1, name: "Dashboard", icon: <DashboardIcon />, permission: "Dashboard" },
      { id: 2, name: "Home", icon: <HomeIcon />, permission: "Home" },
      { id: 3, name: "StudentsDatabase", icon: <PeopleIcon />, permission: "UserManagement" },
      { id: 4, name: "Taaruf", icon: <SchoolIcon />, permission: "Taaruf" },
      { id: 12, name: "AboutSections", icon: <InfoIcon />, permission: "AboutSections" },
      { id: 13, name: "FAQManagement", icon: <LiveHelpIcon />, permission: "FAQManagement" },
      { id: 5, name: "Tazakuraat", icon: <BookIcon />, permission: "Tazakraat" },
      { id: 14, name: "Exhibition", icon: <Collections />, permission: "Exhibition" , hasSubmenu: subMenuItems.Exhibition.length > 0},
      { id: 6, name: "Munasibaat", icon: <EventIcon />, permission: "Munasibaat", hasSubmenu: subMenuItems.Munasibaat.length > 0 },
      { id: 7, name: "Tableghaat", icon: <MenuBookIcon />, permission: "Tableghaat", hasSubmenu: subMenuItems.Tableghaat.length > 0 },
      { id: 8, name: "Users", icon: <PersonIcon />, permission: "UserManagement" },
      { id: 9, name: "TazakraatReview", icon: <ReviewsIcon />, permission: "Review-Tazakuraat" },
      { id: 10, name: "ThemeManagement", icon: <PaletteIcon />, permission: "ThemeManagement" },
      { id: 11, name: "Class Forms", icon: <AssignmentIcon />, permission: "ClassForms" },
      { id: 15, name: "namazTimings", icon: <ScheduleIcon />, permission: "namazTimings" },
      { id: 16, name: "myPosts", icon: <CloudUploadIcon />, permission: "PostUpload" },
      { id: 17, name: "postApproval", icon: <AssignmentTurnedInIcon />, permission: "PostApproval" },
      { id: 18, name: "AuditLogs", icon: <HistoryIcon />, permission: "AuditLogs" },
    ],
    [subMenuItems]
  );

  const filteredMenuItems = useMemo(() => {
    if (userData?.role === "SuperAdmin") {
      return menuItems;
    }
    return menuItems
      .map((item) => {
        const hasPermittedSubmenu = subMenuItems[item.name]?.some((subItem) =>
          userData?.permissions?.includes(subItem.permission)
        );
        return {
          ...item,
          hasSubmenu: subMenuItems[item.name] ? hasPermittedSubmenu : false,
        };
      })
      .filter((item) => userData?.permissions?.includes(item.permission) || item.hasSubmenu);
  }, [menuItems, subMenuItems, userData]);

  const filteredSubMenuItems = useMemo(() => {
    if (userData?.role === "SuperAdmin") {
      return subMenuItems;
    }
    const filtered = {};
    Object.keys(subMenuItems).forEach((key) => {
      filtered[key] = subMenuItems[key].filter((subItem) =>
        userData?.permissions?.includes(subItem.permission)
      );
    });
    return filtered;
  }, [subMenuItems, userData]);

  useEffect(() => {
    const path = location.pathname.split("/")[1];
    const foundMenuItem = filteredMenuItems.find(
      (item) => item.name.toLowerCase().replace(/ /g, "-") === path
    );

    if (foundMenuItem) {
      setActiveSection(foundMenuItem.name);
      setSubmenuOpen(filteredSubMenuItems[foundMenuItem.name] ? foundMenuItem.name : null);
    } else {
      const foundSubMenuItem = Object.values(filteredSubMenuItems)
        .flat()
        .find((subItem) => subItem.nav === path);
      if (foundSubMenuItem) {
        const parentMenu = Object.keys(filteredSubMenuItems).find((key) =>
          filteredSubMenuItems[key].includes(foundSubMenuItem)
        );
        setActiveSection(parentMenu);
        setSubmenuOpen(parentMenu);
      } else {
        setActiveSection("");
        setSubmenuOpen(null);
      }
    }
  }, [location.pathname, filteredMenuItems, filteredSubMenuItems]);

  const handleMainItemClick = useCallback(
    (itemName) => {
      setActiveSection(itemName);
      if (filteredSubMenuItems[itemName]) {
        setSubmenuOpen((prev) => (prev === itemName ? null : itemName));
      } else {
        setSubmenuOpen(null);
        navigate(`/${itemName.toLowerCase().replace(/ /g, "-")}`);
      }
    },
    [navigate, filteredSubMenuItems]
  );

  const handleSubItemClick = useCallback(
    (subItemNav) => {
      setActiveSection(subItemNav);
      navigate(`/${subItemNav}`);
    },
    [navigate]
  );

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      logout();
      navigate('/');
    }
  };

  const formatDisplayName = (name) => name.replace(/([a-z])([A-Z])/g, '$1 $2');

  return (
    <Drawer
      variant="permanent"
      anchor="left"
      sx={{
        width: 260,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: { 
          width: 260, 
          boxSizing: 'border-box', 
          background: isDark ? 'rgba(15, 23, 42, 0.88)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRight: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
          color: isDark ? '#f8fafc' : '#0f172a',
          transition: 'background-color 0.3s ease, border-color 0.3s ease'
        },
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Branding Header */}
        <div className={`p-5 border-b flex flex-col items-center justify-center ${
          isDark ? 'border-white/10 from-white/5' : 'border-slate-200 from-slate-100'
        } bg-gradient-to-b to-transparent`}>
          <div className={`relative p-2 rounded-2xl shadow-lg mb-2 border ${
            isDark ? 'bg-white/10 border-white/15' : 'bg-slate-100 border-slate-200'
          }`}>
            <img src={logo} alt="Logo" className="w-12 h-12 object-contain" />
          </div>
          <div className="font-extrabold text-base tracking-wide gradient-text text-center">
            Mushtaqan e Noor
          </div>
          <div className={`text-[11px] font-medium tracking-wider uppercase mt-0.5 ${
            isDark ? 'text-slate-400' : 'text-slate-500'
          }`}>
            Admin CMS Portal
          </div>
        </div>

        {/* Scrollable Navigation List */}
        <div className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          <List component="nav" disablePadding className="space-y-1">
            {filteredMenuItems.map((item) => {
              const isSelected = activeSection === item.name || location.pathname.includes(item.name.toLowerCase().replace(/ /g, "-"));
              return (
                <React.Fragment key={item.id}>
                  <ListItem
                    button
                    onClick={() => handleMainItemClick(item.name)}
                    sx={{
                      borderRadius: '0.75rem',
                      mb: 0.5,
                      py: 1.1,
                      px: 2,
                      background: isSelected 
                        ? (isDark ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.25) 0%, rgba(6, 182, 212, 0.25) 100%)' : 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(6, 182, 212, 0.15) 100%)')
                        : 'transparent',
                      border: isSelected 
                        ? (isDark ? '1px solid rgba(99, 102, 241, 0.4)' : '1px solid rgba(99, 102, 241, 0.3)')
                        : '1px solid transparent',
                      color: isSelected 
                        ? (isDark ? '#ffffff' : '#4f46e5') 
                        : (isDark ? '#94a3b8' : '#475569'),
                      transition: 'all 0.25s ease',
                      '&:hover': {
                        background: isSelected 
                          ? (isDark ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.35) 0%, rgba(6, 182, 212, 0.35) 100%)' : 'rgba(99, 102, 241, 0.2)')
                          : (isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)'),
                        color: isDark ? '#ffffff' : '#0f172a',
                        transform: 'translateX(3px)'
                      }
                    }}
                  >
                    <ListItemIcon 
                      sx={{ 
                        color: isSelected ? '#38bdf8' : (isDark ? '#64748b' : '#94a3b8'), 
                        minWidth: 38,
                        transition: 'color 0.2s ease'
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={formatDisplayName(item.name)} 
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: isSelected ? 700 : 500
                      }}
                    />
                    {item.hasSubmenu && filteredSubMenuItems[item.name]?.length > 0 && (
                      submenuOpen === item.name ? <ExpandLess sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 18 }} /> : <ExpandMore sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 18 }} />
                    )}
                  </ListItem>

                  {item.hasSubmenu && filteredSubMenuItems[item.name]?.length > 0 && (
                    <Collapse in={submenuOpen === item.name} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding className="pl-4 py-1 space-y-1">
                        {filteredSubMenuItems[item.name].map((subItem) => {
                          const isSubSelected = `/${subItem.nav}` === location.pathname;
                          return (
                            <ListItem
                              button
                              key={subItem.id}
                              selected={isSubSelected}
                              onClick={() => handleSubItemClick(subItem.nav)}
                              sx={{
                                borderRadius: '0.625rem',
                                py: 0.8,
                                px: 2,
                                color: isSubSelected ? '#38bdf8' : (isDark ? '#94a3b8' : '#64748b'),
                                background: isSubSelected ? (isDark ? 'rgba(56, 189, 248, 0.12)' : 'rgba(56, 189, 248, 0.15)') : 'transparent',
                                border: isSubSelected ? '1px solid rgba(56, 189, 248, 0.3)' : '1px solid transparent',
                                '&:hover': {
                                  background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                                  color: isDark ? '#ffffff' : '#0f172a'
                                }
                              }}
                            >
                              <ListItemText 
                                primary={formatDisplayName(subItem.name)} 
                                primaryTypographyProps={{
                                  fontSize: '0.8rem',
                                  fontWeight: isSubSelected ? 600 : 400
                                }}
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })}
          </List>
        </div>

        {/* User Card & Actions Footer */}
        <div className={`p-4 border-t space-y-3 backdrop-blur-md ${
          isDark ? 'border-white/10 bg-slate-950/60' : 'border-slate-200 bg-slate-100/80'
        }`}>
          <div className={`flex items-center justify-between p-2 rounded-xl border ${
            isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200 shadow-sm'
          }`}>
            <div className="flex items-center space-x-2 overflow-hidden">
              <Avatar 
                sx={{ 
                  width: 32, 
                  height: 32, 
                  bg: 'linear-gradient(135deg, #6366f1, #06b6d4)',
                  fontSize: '0.8rem',
                  fontWeight: 700
                }}
              >
                {userData?.email ? userData.email.charAt(0).toUpperCase() : 'A'}
              </Avatar>
              <div className="overflow-hidden">
                <div className={`text-xs font-semibold truncate ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                  {userData?.email || 'Admin User'}
                </div>
                <div className="text-[10px] font-medium text-indigo-500 uppercase tracking-wider">
                  {userData?.role || 'Administrator'}
                </div>
              </div>
            </div>

            <Tooltip title={`Toggle ${isDark ? 'Light' : 'Dark'} Theme`}>
              <IconButton size="small" onClick={toggleTheme} sx={{ color: isDark ? '#f59e0b' : '#6366f1' }}>
                {isDark ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </div>

          <Button
            variant="contained"
            className="glass-btn-danger"
            startIcon={<LogoutIcon fontSize="small" />}
            fullWidth
            onClick={handleLogout}
            sx={{
              py: 0.9,
              fontSize: '0.8rem',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
              }
            }}
          >
            LOGOUT
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default NavBar;